import React, { useState, useEffect } from "react";
import { Route, Routes, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import {
  AppShell,
  Burger,
  Button,
  Divider,
  Flex,
  Group,
  Title,
  Modal,
  ScrollArea,
  Text,
  UnstyledButton,
  useMantineTheme,
  Anchor,
  Stack,
  Box,
} from "@mantine/core";
import { Toaster } from "react-hot-toast";
import { IconBomb, IconTestPipe2Filled } from "@tabler/icons-react";
import { ErrorBoundary } from "react-error-boundary";

import logo from "@assets/images/logo.png";
import entityColor from "@util/entityColor";
import entityIcon from "@util/entityIcon";
import {
  Nameplate,
  Nav,
  NavGroup,
  SuspenseWrapper,
  LanguagePicker,
} from "@components/shared";
import { AlertWidget } from "@components/Alert";
import {
  ChatAwareAppShell,
  ChatProvider,
  ChatTray,
  ChatWidget,
} from "@components/Chat";
import { ReportDisplay } from "@components/Report";
import { RealtimeProvider } from "@lib/realtime";
import appStore from "@duck/app-store";

import classes from "./AdminContainer.module.css";

import {
  AdminDetail,
  AdminIndex,
  Dashboard,
  ProfileDetail,
  ProfileIndex,
  UserDetail,
  UserIndex,
  EvaluationIndex,
  EvaluationDetail,
  SurveyIndex,
  SurveyDetail,
  OrganizationIndex,
  OrganizationDetail,
  QuestionIndex,
  QuestionDetail,
  RequestIndex,
  RequestDetail,
  TestingZone,
  ClearscreenReportPage,
  CompareIndex,
  RoleDetail,
  RoleIndex,
  PermissionDetail,
  ConceptualRootIndex,
  ConceptualRootDetail,
  AssessmentInstrumentIndex,
  AssessmentInstrumentDetail,
  AssessmentIndex,
  AssessmentDetail,
  InstrumentItemIndex,
  AlertIndex,
} from "@pages/Admin";
import { Can } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import usePermission from "@util/usePermission";

export default function AdminContainer() {
  const avatarUrl = null;
  const [opened, setOpened] = useState(false);
  const { pathname } = useLocation();
  const { admin } = appStore(
    (state) => ({
      admin: state.admin,
    }),
    shallow
  );
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const canViewRoles = usePermission(PERMISSIONS.ROLES_VIEW);
  const canViewConceptualRoots = usePermission(PERMISSIONS.CONCEPTUAL_ROOTS_VIEW);
  const canViewInstruments = usePermission(
    PERMISSIONS.ASSESSMENT_INSTRUMENTS_VIEW
  );
  const canViewAssessments = usePermission(PERMISSIONS.ASSESSMENTS_VIEW);
  const canApproveAssessments = usePermission(PERMISSIONS.ASSESSMENTS_APPROVE);
  const canViewOccupationalProfiles = usePermission(
    PERMISSIONS.OCCUPATIONAL_PROFILES_VIEW
  );

  useEffect(() => {
    setOpened(false);
  }, [pathname]);

  if (/^\/report-clearscreen\/[^/]+\/[^/]+/.test(pathname)) {
    return (
      <>
        <Toaster />
        <ClearscreenReportPage />
      </>
    );
  }

  const peopleLinks = [
    {
      text: t("admin_plural"),
      to: "admins",
      icon: entityIcon.admin(),
      color: entityColor.admin,
    },
    {
      text: t("user_plural"),
      to: "users",
      icon: entityIcon.user(),
      color: entityColor.user,
    },
  ];

  if (canViewRoles) {
    peopleLinks.push({
      text: t("role_plural"),
      to: "roles",
      icon: entityIcon.role(),
      color: entityColor.role,
    });
  }

  const navLinks = [
    {
      text: t("dashboard"),
      to: "",
      icon: entityIcon.dashboard(),
    },
    {
      text: t("cs:organization_plural"),
      to: "organizations",
      icon: entityIcon.company(),
    },
  ];

  if (canViewConceptualRoots) {
    navLinks.push({
      text: t("conceptual_root_plural"),
      to: "conceptual-roots",
      icon: entityIcon.profile(),
      color: entityColor.role,
    });
    navLinks.push({
      text: t("instrument_item_plural"),
      to: "instrument-items",
      icon: entityIcon.question(),
      color: entityColor.role,
    });
  }
  if (canViewInstruments) {
    navLinks.push({
      text: t("assessment_instrument_plural"),
      to: "instruments",
      icon: entityIcon.survey(),
      color: entityColor.engagement,
    });
  }
  if (canViewAssessments) {
    navLinks.push({
      text: t("assessment_plural"),
      to: "assessments",
      icon: entityIcon.evaluation(),
      color: entityColor.engagement,
    });
  }

  if (canApproveAssessments) {
    navLinks.push({
      text: t("assignment_request_plural"),
      to: "requests",
      icon: entityIcon.request(),
    });
  }

  if (canViewOccupationalProfiles) {
    navLinks.push({
      text: t("cs:profile_plural"),
      to: "profiles",
      icon: entityIcon.profile(),
    });
  }

  navLinks.push(
    {
      text: t("cs:evaluation_plural"),
      to: "evaluations",
      icon: entityIcon.evaluation(),
    },
    {
      text: "Testing Zone",
      to: "testing-zone",
      icon: <IconTestPipe2Filled size={16} />,
    },
    {
      text: "In progress",
      to: "in-progress",
      icon: <IconTestPipe2Filled size={16} />,
    }
  );

  return (
    <RealtimeProvider enabled>
      <ChatProvider>
      <div>
        <ChatAwareAppShell
          opened={opened}
          padding="md"
          header={{ height: 65 }}
        >
          <AppShell.Header
            height={65}
            pl="xs"
            pr="xs"
            style={{
              background: `var(--mantine-color-gray-1)`,
            }}
          >
            <Group
              justify="flex-end"
              align="center"
              style={{
                height: "100%",
              }}
            >
              <Flex align="center" gap="md" style={{ flexGrow: 1 }}>
                <div className={classes.burger}>
                  <Burger
                    opened={opened}
                    onClick={() => setOpened(!opened)}
                    size="lg"
                    color={theme.colors.dark[1]}
                    mr="xl"
                  />
                </div>
                <div className={classes.logo}>
                  <img
                    src={logo}
                    alt="Logo"
                    style={{
                      width: "80px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Flex>
              <div className={classes.right}>
                <Group position="right" align="flexEnd">
                  <Can permission={PERMISSIONS.CHAT_USE}>
                    <ChatWidget />
                  </Can>
                  <AlertWidget />
                  <LanguagePicker />
                  <Nameplate
                    avatarUrl={avatarUrl}
                    title={admin?.full_name}
                    subtitle="Admin"
                  />
                </Group>
              </div>
            </Group>
          </AppShell.Header>
          <AppShell.Navbar pt="md" hidden={!opened}>
            {/* <Navbar.Section style={{ background: "#000" }} p="lg">
              <Image
                src={logo}
                alt="clearscreen"
                width={120}
                style={{ margin: "0 auto" }}
              />
            </Navbar.Section> */}
            <ScrollArea h="100vh" type="scroll" mt="md">
              <NavGroup
                title="People"
                links={peopleLinks.map((m, i) => ({
                  ...m,
                  key: i,
                  to: `/${m.to}`,
                }))}
              />
              <Nav
                links={navLinks.map((m) => ({
                  ...m,
                  to: `/${m.to}`,
                }))}
              />
              {/* {["dev", "qa", "demo"].includes(import.meta.env.VITE_ENV) && (
              <ClearUserData />
            )} */}
            </ScrollArea>
            <div className={classes.nameplate}>
              <Group mb="sm">
                <div>
                  <Nameplate
                    title={admin?.full_name}
                    subtitle={t("admin_singular")}
                  />
                </div>
              </Group>
            </div>
          </AppShell.Navbar>
          <AppShell.Main
            style={(theme) => ({
              background: theme.colors.gray[0],
            })}
          >
            <ErrorBoundary
              fallback={
                <div>
                  <Title>That's not good!</Title>
                  <Text>Something went wrong and we're going to fix it.</Text>
                </div>
              }
            >
              <SuspenseWrapper>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="/admins" element={<AdminIndex />} />
                  <Route path="/admins/:id/*" element={<AdminDetail />} />
                  <Route path="/users" element={<UserIndex />} />
                  <Route path="/users/:id/*" element={<UserDetail />} />
                  <Route path="/profile" element={<AdminDetail isProfile />} />
                  <Route
                    path="/profiles"
                    element={
                      <Can permission={PERMISSIONS.OCCUPATIONAL_PROFILES_VIEW}>
                        <ProfileIndex />
                      </Can>
                    }
                  />
                  <Route
                    path="/profiles/:id/*"
                    element={
                      <Can permission={PERMISSIONS.OCCUPATIONAL_PROFILES_VIEW}>
                        <ProfileDetail />
                      </Can>
                    }
                  />
                  <Route path="/evaluations" element={<EvaluationIndex />} />
                  <Route
                    path="/evaluations/:id/*"
                    element={<EvaluationDetail />}
                  />
                  <Route path="/surveys" element={<SurveyIndex />} />
                  <Route path="/surveys/:id/*" element={<SurveyDetail />} />
                  <Route
                    path="/organizations"
                    element={<OrganizationIndex />}
                  />
                  <Route
                    path="/organizations/:id/*"
                    element={<OrganizationDetail />}
                  />
                  <Route path="/questions" element={<QuestionIndex />} />
                  <Route path="/questions/:id/*" element={<QuestionDetail />} />
                  <Route
                    path="/instrument-items"
                    element={<InstrumentItemIndex />}
                  />
                  <Route
                    path="/requests"
                    element={
                      <Can permission={PERMISSIONS.ASSESSMENTS_APPROVE}>
                        <RequestIndex />
                      </Can>
                    }
                  />
                  <Route path="/requests/:id/*" element={<RequestDetail />} />
                  <Route path="/alerts" element={<AlertIndex />} />
                  <Route path="/testing-zone" element={<TestingZone />} />
                  <Route path="/in-progress" element={<InProgress />} />
                  <Route
                    path="/report-compare/:id"
                    element={<ReportDisplay meta={{ type: "compare" }} />}
                  />
                  <Route
                    path="/report-360/:id"
                    element={
                      <ReportDisplay
                        meta={{
                          type: "360",
                        }}
                      />
                    }
                  />
                  <Route
                    path="/report-clearscreen/:id"
                    element={
                      <ReportDisplay
                        meta={{
                          type: "clearscreen",
                        }}
                      />
                    }
                  />
                  <Route
                    path="/report-competency/:id"
                    element={
                      <ReportDisplay
                        meta={{
                          type: "competency",
                        }}
                      />
                    }
                  />
                  <Route
                    path="/report-engagement/:id"
                    element={
                      <ReportDisplay
                        meta={{
                          type: "engagement",
                        }}
                      />
                    }
                  />
                  <Route
                    path="/roles"
                    element={
                      <Can permission={PERMISSIONS.ROLES_VIEW}>
                        <RoleIndex />
                      </Can>
                    }
                  />
                  <Route
                    path="/roles/:id/*"
                    element={
                      <Can permission={PERMISSIONS.ROLES_VIEW}>
                        <RoleDetail />
                      </Can>
                    }
                  />
                  <Route
                    path="/permissions/:id"
                    element={
                      <Can permission={PERMISSIONS.ROLES_VIEW}>
                        <PermissionDetail />
                      </Can>
                    }
                  />
                  <Route
                    path="/conceptual-roots"
                    element={
                      <Can permission={PERMISSIONS.CONCEPTUAL_ROOTS_VIEW}>
                        <ConceptualRootIndex />
                      </Can>
                    }
                  />
                  <Route
                    path="/conceptual-roots/:id/*"
                    element={
                      <Can permission={PERMISSIONS.CONCEPTUAL_ROOTS_VIEW}>
                        <ConceptualRootDetail />
                      </Can>
                    }
                  />
                  <Route
                    path="/instruments"
                    element={
                      <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_VIEW}>
                        <AssessmentInstrumentIndex />
                      </Can>
                    }
                  />
                  <Route
                    path="/instruments/:id/*"
                    element={
                      <Can permission={PERMISSIONS.ASSESSMENT_INSTRUMENTS_VIEW}>
                        <AssessmentInstrumentDetail />
                      </Can>
                    }
                  />
                  <Route
                    path="/assessments"
                    element={
                      <Can permission={PERMISSIONS.ASSESSMENTS_VIEW}>
                        <AssessmentIndex />
                      </Can>
                    }
                  />
                  <Route
                    path="/assessments/:id"
                    element={
                      <Can permission={PERMISSIONS.ASSESSMENTS_VIEW}>
                        <AssessmentDetail />
                      </Can>
                    }
                  />
                </Routes>
              </SuspenseWrapper>
            </ErrorBoundary>
          </AppShell.Main>
        </ChatAwareAppShell>
        <ChatTray />
        <Toaster />
      </div>
      </ChatProvider>
    </RealtimeProvider>
  );
}

function ClearUserData() {
  const [isOpen, setOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  function onClose() {
    if (loading) return;
    setOpen(false);
    setShowConfirm(false);
  }

  function onConfirmClick() {
    setLoading(true);

    axios
      .post(`/testing/reset-actions/`, {})
      .then(() => {
        window.location.reload();
      })
      .catch((err) => {
        setLoading(false);
        alert(err);
      });
  }

  return (
    <React.Fragment>
      <Divider mt="md" mb="md" />
      <UnstyledButton
        style={{
          display: "block",
          width: "100%",
          padding: "var(--mantine-spacing-xs)",
          borderRadius: "var(--mantine-radius-sm)",
          // "&:hover": {
          //   backgroundColor:
          //     theme.colorScheme === "dark"
          //       ? theme.colors.dark[6]
          //       : theme.colors.gray[0],
          // },
        }}
        onClick={() => setOpen(true)}
      >
        <Group>
          <IconBomb size={12} />
          <Text size="xs">Don't Click</Text>
        </Group>
      </UnstyledButton>
      <Modal opened={isOpen} onClose={onClose}>
        <Group justify="center" mb="sm">
          <IconBomb size={60} />
        </Group>
        {!showConfirm ? (
          <React.Fragment>
            <Text align="center" fw={600}>
              You are about to clear all user data. Are you sure you want to
              proceed?
            </Text>
            <Button fullWidth mt="lg" onClick={onClose} color="green">
              Nope! Get me out of here
            </Button>
            <Group justify="center">
              <Button
                mt="sm"
                variant="subtle"
                color="gray"
                onClick={() => setShowConfirm(true)}
              >
                I'm sure
              </Button>
            </Group>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Text align="center" fw={600} mb="sm">
              Current ENV: {import.meta.env.VITE_ENV}
            </Text>
            <Text align="center" fw={600}>
              This is really going to f*** s*** up and delete all users and user
              interactions for {import.meta.env.VITE_ENV}. Are you sure you want
              to proceed?
            </Text>
            <Button fullWidth mt="lg" onClick={onClose} color="green">
              Nope! Get me out of here
            </Button>
            <Group justify="center">
              <Button
                mt="sm"
                variant="subtle"
                color="gray"
                loading={loading}
                onClick={onConfirmClick}
              >
                I'm sure
              </Button>
            </Group>
          </React.Fragment>
        )}
      </Modal>
    </React.Fragment>
  );
}

const InProgress = () => {
  return (
    <Stack gap="sm">
      <Anchor component={Link} to="/report-360/1">
        360
      </Anchor>
      <Anchor component={Link} to="/report-clearscreen/1">
        Clearscreen
      </Anchor>
      <Anchor component={Link} to="/report-competency/1">
        Competency
      </Anchor>
      <Anchor component={Link} to="/report-engagement/1">
        Engagement
      </Anchor>
      <Anchor component={Link} to="/report-compare/1">
        Compare
      </Anchor>
    </Stack>
  );
};
